import {
  Alert,
  Button,
  PageHeader,
  Skeleton,
  StatusBadge,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  useToast,
} from '@catering/ui';
import type { MenuBadgeStatus } from '@catering/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { Archive, AlertTriangle, Info, StickyNote, Tag, UtensilsCrossed } from 'lucide-react';
import type { JSX } from 'react';

import { ApiError } from '../../api/client';
import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import {
  archiveMenu,
  getMenu,
  listAllergens,
  listDietaryTags,
  listServiceTypes,
  type MenuDetail,
} from '../../menus/api';
import { formatBasePrice } from '../../menus/format-base-price';
import { MenuAllergensTab } from './components/menu-allergens-tab';
import { MenuDietaryTagsTab } from './components/menu-dietary-tags-tab';
import { MenuInfoTab } from './components/menu-info-tab';
import { MenuPreparationNotesTab } from './components/menu-preparation-notes-tab';
import { mapMenuPatchError } from './menu-form-schema';

export type MenuDetailTab = 'informacion' | 'etiquetas' | 'alergenos' | 'notas';

const menuDetailRoute = getRouteApi('/protected/menus/$menuId');

const TAB_LABELS: Record<MenuDetailTab, string> = {
  informacion: 'Información',
  etiquetas: 'Etiquetas dietarias',
  alergenos: 'Alérgenos',
  notas: 'Notas de preparación',
};

const TAB_ICONS: Record<MenuDetailTab, typeof Info> = {
  informacion: Info,
  etiquetas: Tag,
  alergenos: AlertTriangle,
  notas: StickyNote,
};

/**
 * `menus/spec.md` → "Pantallas de alta, edición y detalle en back-office".
 * Tabs are local state driven by the `tab` search param, not sub-routes
 * (`design.md` §7, same convention as `/customers/:customerId`).
 */
export function MenuDetailPage(): JSX.Element {
  const { menuId } = menuDetailRoute.useParams();
  const { tab } = menuDetailRoute.useSearch();
  const navigate = menuDetailRoute.useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const currentUser = useCurrentUser();
  const canArchive = hasPermission(currentUser.data, 'menu.delete');

  const query = useQuery({
    queryKey: ['menu', menuId],
    queryFn: () => getMenu(menuId),
  });
  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });
  const dietaryTagsQuery = useQuery({ queryKey: ['dietary-tags'], queryFn: listDietaryTags });
  const allergensQuery = useQuery({ queryKey: ['allergens'], queryFn: listAllergens });

  const archiveMutation = useMutation({
    mutationFn: () => archiveMenu(menuId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['menu', menuId] });
      await queryClient.invalidateQueries({ queryKey: ['menus'] });
      toast('success', 'Menú archivado');
    },
  });

  function goToTab(next: MenuDetailTab): void {
    void navigate({ search: (prev) => ({ ...prev, tab: next }) });
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) {
      return (
        <Alert tone="danger" title="Menú no encontrado">
          <Link to="/menus" className="underline">
            Volver al listado
          </Link>
        </Alert>
      );
    }

    return (
      <Alert tone="danger" title="No pudimos cargar el menú">
        <div className="flex items-center justify-between gap-4">
          <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
            Reintentar
          </Button>
        </div>
      </Alert>
    );
  }

  const menu = query.data;
  const isArchived = menu.deletedAt !== null;
  const serviceTypeName = serviceTypesQuery.data?.find(
    (serviceType) => serviceType.id === menu.serviceTypeId,
  )?.name;
  const description = `${menu.code} · ${serviceTypeName ?? '—'} · ${formatBasePrice(menu.basePriceCents)}`;
  // `menus/spec.md` → "Notas de preparación visibles solo para cocina": the
  // key is entirely absent from the response, not `null`, when the caller
  // cannot see it. That absence is the only signal the tab needs.
  const hasPreparationNotes = 'preparationNotes' in menu;
  const visibleTabs = (Object.keys(TAB_LABELS) as MenuDetailTab[]).filter(
    (key) => key !== 'notas' || hasPreparationNotes,
  );

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={UtensilsCrossed}
        title={menu.name}
        description={description}
        actions={
          <div className="flex items-center gap-3">
            <StatusBadge domain="menu" status={menuBadgeStatus(menu)} />
            {!isArchived && canArchive ? (
              <Button
                variant="destructive"
                icon={Archive}
                loading={archiveMutation.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      `¿Archivar el menú "${menu.name}"? Esta acción no se puede deshacer.`,
                    )
                  ) {
                    archiveMutation.mutate();
                  }
                }}
              >
                Archivar
              </Button>
            ) : null}
          </div>
        }
      />

      {isArchived ? (
        <Alert tone="warning" title="Menú archivado">
          Este menú está archivado: se retiró del catálogo disponible para nuevos pedidos y no puede
          editarse.
        </Alert>
      ) : null}

      {archiveMutation.isError ? (
        <Alert tone="danger">{mapMenuPatchError(archiveMutation.error)}</Alert>
      ) : null}

      <Tabs value={tab} onValueChange={(value) => goToTab(value as MenuDetailTab)}>
        <TabList aria-label="Pestañas del menú">
          {visibleTabs.map((key) => (
            <Tab key={key} value={key} icon={TAB_ICONS[key]}>
              {TAB_LABELS[key]}
            </Tab>
          ))}
        </TabList>

        <TabPanel value="informacion">
          <MenuInfoTab
            menu={menu}
            serviceTypes={serviceTypesQuery.data ?? []}
            onRefetch={() => void query.refetch()}
          />
        </TabPanel>
        <TabPanel value="etiquetas">
          <MenuDietaryTagsTab menu={menu} dietaryTags={dietaryTagsQuery.data ?? []} />
        </TabPanel>
        <TabPanel value="alergenos">
          <MenuAllergensTab menu={menu} allergens={allergensQuery.data ?? []} />
        </TabPanel>
        {hasPreparationNotes ? (
          <TabPanel value="notas">
            <MenuPreparationNotesTab menu={menu} />
          </TabPanel>
        ) : null}
      </Tabs>
    </section>
  );
}

function menuBadgeStatus(menu: MenuDetail): MenuBadgeStatus {
  if (menu.deletedAt !== null) return 'ARCHIVED';
  return menu.isActive ? 'ACTIVE' : 'INACTIVE';
}
